import { cloneElement, isValidElement, useEffect, useMemo, useRef } from "react";

import {
  Box,
  extractStyleProps,
  Group,
  GroupProps,
  InputProps,
  Menu,
  UnstyledButton,
} from "@mantine/core";

import { IconChevronDown } from "@tabler/icons-react";
import classNames from "classnames";

import { TextWrapper } from "../TextWrapper";

import classes from "./MultiSearchInputWrapper.module.css";

interface SearchOption {
  label: string;
  value: string;
}

interface MultiSearchInputWrapperProps extends GroupProps {
  options: SearchOption[];
  selected: SearchOption["value"];
  menuWidth?: string | number;
  menuTargetTestId?: string;
  onChangeOption: (option: SearchOption["value"]) => void;
  label?: string;
  error?: React.ReactNode;
  children: React.ReactElement<InputProps>;
}

export default function MultiSearchInputWrapper({
  options,
  selected,
  onChangeOption,
  menuWidth,
  menuTargetTestId,
  children,
  label,
  error,
  ...props
}: MultiSearchInputWrapperProps) {
  // Size and spacing apply to the root, the element the page lays out; the row fills it.
  const { styleProps, rest } = extractStyleProps(props);
  const labelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedLabel = useMemo(
    () => options.find((option) => option.value === selected)?.label ?? options[0].label,
    [options, selected],
  );

  useEffect(() => {
    if (labelRef.current && containerRef.current && label) {
      const labelWidth = labelRef.current.offsetWidth;
      containerRef.current.style.setProperty("--label-width", `${labelWidth}px`);
    }
  }, [label]);

  const childrenWithInputClassNames = useMemo(() => {
    if (!children) return children;

    if (isValidElement(children)) {
      return cloneElement(children, {
        flex: 1,
        classNames: { input: classNames(classes.input, { [classes.error]: !!error }) },
      });
    }

    return children;
  }, [children, error]);

  return (
    <Box ref={containerRef} {...styleProps}>
      <Group
        gap="0"
        w="100%"
        align="center"
        className={classNames(classes.root, { [classes.error]: !!error })}
        {...rest}
      >
        {label && (
          <TextWrapper mr="xs" ref={labelRef} className={classes.label}>
            {label}
          </TextWrapper>
        )}
        <Menu withArrow width={menuWidth} position="bottom-start">
          <Menu.Target>
            <UnstyledButton className={classes.menuTarget} data-testid={menuTargetTestId}>
              <TextWrapper c="burgundy.9">{selectedLabel}</TextWrapper>
              <IconChevronDown size={16} color="var(--mantine-color-burgundy-9)" />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {options.map((option) => (
              <Menu.Item
                key={option.value}
                disabled={option.value === selected}
                onClick={() => onChangeOption(option.value)}
                data-testid={menuTargetTestId ? `${menuTargetTestId}-${option.value}` : undefined}
              >
                {option.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
        {childrenWithInputClassNames}
      </Group>
      {error && (
        <TextWrapper
          mt="2"
          fz="xs"
          c="red.6"
          aria-role="error"
          className={classNames({
            [classes.errorWithLabel]: !!label,
          })}
        >
          {error}
        </TextWrapper>
      )}
    </Box>
  );
}
